import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/formatApiError";
import { Checkbox } from "@/components/ui/checkbox";
import { TwoFactorStep } from "@/components/auth/TwoFactorStep";
import { GoogleButton, OrSeparator } from "@/components/auth/GoogleButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { LogIn, Loader2, Eye, EyeOff } from "lucide-react";

export const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const { login, completeTwoFactor, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Where to send user after login (preserved from ProtectedRoute redirect, e.g. /sav/intervention/<id>)
  const redirectTo = location?.state?.from || "/";

  useEffect(() => {
    const initApp = async () => {
      setInitializing(false);
    };
    initApp();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, navigate, redirectTo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const res = await login(email, password, rememberMe);
      if (res?.twoFactor) {
        setChallenge(res.twoFactor);
        return;
      }
      toast.success("Connexion réussie");
      navigate(redirectTo, { replace: true });
    } catch (error) {
      toast.error(formatApiError(error, "Erreur de connexion"));
    } finally {
      setLoading(false);
    }
  };

  if (initializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-4 text-muted-foreground">Initialisation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      {/* Left side - Image */}
      <div
        className="login-image hidden lg:block"
        style={{
          backgroundImage: `url(https://images.unsplash.com/photo-1764536569752-5fd7ada167bf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwYXJjaGl0ZWN0dXJlJTIwY29uY3JldGUlMjB3b29kfGVufDB8fHx8MTc3MjQ1ODgwNnww&ixlib=rb-4.1.0&q=85)`
        }}
      >
        <div className="relative z-10 h-full flex flex-col justify-end p-12 text-white">
          <h2 className="font-heading text-4xl font-bold mb-4">
            Gérez vos chantiers en toute simplicité
          </h2>
          <p className="text-lg text-white/80 max-w-md">
            Suivez vos commandes, planifiez vos interventions et gérez vos équipes commerciales.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <Card className="w-full max-w-md border-0 shadow-none lg:shadow-sm lg:border">
          <CardHeader className="space-y-2 text-center">
            <img
              src="/logo-bati-assistant.svg"
              alt="Bati Assistant"
              className="mx-auto h-20 w-20 mb-2"
              data-testid="login-logo"
            />
            <CardTitle className="font-heading text-2xl">Bati Assistant</CardTitle>
            <CardDescription>
              Connectez-vous pour accéder à votre espace
            </CardDescription>
          </CardHeader>
          <CardContent>
            {challenge ? (
              <TwoFactorStep
                challenge={challenge}
                onVerify={async (id, code) => {
                  await completeTwoFactor(id, code);
                  toast.success("Connexion réussie");
                  navigate(redirectTo, { replace: true });
                }}
                onCancel={() => setChallenge(null)}
              />
            ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="votre@email.fr"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="login-password-input"
                    className="h-11 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    tabIndex={-1}
                    aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                    data-testid="login-password-toggle"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                  <Checkbox
                    checked={rememberMe}
                    onCheckedChange={(v) => setRememberMe(v === true)}
                    data-testid="login-remember-me-checkbox"
                  />
                  Rester connecté 30 jours
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary hover:underline"
                  data-testid="login-forgot-password-link"
                >
                  Mot de passe oublié ?
                </Link>
              </div>
              <Button
                type="submit"
                className="w-full h-11 btn-press"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <LogIn className="h-4 w-4 mr-2" />
                )}
                Se connecter
              </Button>
            </form>
            )}
            {!challenge && (
              <div className="mt-6 space-y-6">
                <OrSeparator />
                <GoogleButton returnPath="/login" rememberMe={rememberMe} />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
